import { z } from 'zod'

import {
  MEASUREMENT_ZONES,
  type MeasurementFormMode,
  type MeasurementInputRules,
  type MeasurementValues,
} from '@/features/compliance/types'

const phaseSchema = z.object({ upper: z.string(), middle: z.string(), lower: z.string() })
const inputSchema = z.object({
  before: phaseSchema,
  after: phaseSchema,
  correctionReason: z.string(),
})
export type MeasurementInput = z.infer<typeof inputSchema>

type Decimal = { coefficient: bigint; scale: number }

// Keep decimal text exact. Number()/parseFloat() would round values before validation.
function decimal(value: string): Decimal | undefined {
  const text = value.trim()
  if (text.length > 64 || !/^-?\d+(?:[.,]\d+)?$/.test(text)) return undefined
  const [integer, fraction = ''] = text.replace(',', '.').split('.')
  const significantFraction = fraction.replace(/0+$/, '')
  return { coefficient: BigInt(integer + significantFraction), scale: significantFraction.length }
}

function aligned(left: Decimal, right: Decimal) {
  const scale = Math.max(left.scale, right.scale)
  return {
    left: left.coefficient * 10n ** BigInt(scale - left.scale),
    right: right.coefficient * 10n ** BigInt(scale - right.scale),
    scale,
  }
}

function canonical({ coefficient, scale }: Decimal): string {
  if (coefficient === 0n) return '0'
  const sign = coefficient < 0n ? '-' : ''
  const digits = (coefficient < 0n ? -coefficient : coefficient).toString().padStart(scale + 1, '0')
  if (scale === 0) return sign + digits
  return (sign + digits.slice(0, -scale) + '.' + digits.slice(-scale))
    .replace(/0+$/, '')
    .replace(/\.$/, '')
}

function checkedRules(rules: MeasurementInputRules) {
  const minimum = decimal(rules.minimum)
  const maximum = decimal(rules.maximum)
  if (
    !rules.unit.trim() ||
    !Number.isSafeInteger(rules.decimalPlaces) ||
    rules.decimalPlaces < 0 ||
    !minimum ||
    !maximum
  ) {
    throw new Error('Invalid measurement input rules')
  }
  const bounds = aligned(minimum, maximum)
  if (bounds.left > bounds.right) throw new Error('Invalid measurement input rules')
  return { minimum, maximum }
}

function validationMessage(
  value: Decimal,
  rules: MeasurementInputRules,
  bounds: ReturnType<typeof checkedRules>,
) {
  if (value.scale > rules.decimalPlaces)
    return `Bitte geben Sie höchstens ${rules.decimalPlaces} Nachkommastellen ein.`
  const minimum = aligned(value, bounds.minimum)
  const maximum = aligned(value, bounds.maximum)
  if (minimum.left < minimum.right || maximum.left > maximum.right) {
    return `Bitte geben Sie einen Wert zwischen ${rules.minimum.replace('.', ',')} und ${rules.maximum.replace('.', ',')} ${rules.unit} ein.`
  }
  return undefined
}

export function createMeasurementSchema(rules: MeasurementInputRules, mode: MeasurementFormMode) {
  const bounds = checkedRules(rules)
  return inputSchema.superRefine((input, context) => {
    let populated = false
    for (const phase of ['before', 'after'] as const) {
      for (const zone of MEASUREMENT_ZONES) {
        const text = input[phase][zone].trim()
        if (!text) continue
        populated = true
        const value = decimal(text)
        const message = value
          ? validationMessage(value, rules, bounds)
          : 'Bitte geben Sie eine Dezimalzahl ohne Tausendertrennzeichen ein.'
        if (message) context.addIssue({ code: 'custom', path: [phase, zone], message })
      }
    }
    if (!populated)
      context.addIssue({
        code: 'custom',
        path: ['before'],
        message: 'Bitte erfassen Sie mindestens einen Messwert.',
      })
    if (mode === 'correction' && !input.correctionReason.trim()) {
      context.addIssue({
        code: 'custom',
        path: ['correctionReason'],
        message: 'Bitte begründen Sie die Korrektur.',
      })
    }
  })
}

export function toMeasurementValues(input: MeasurementInput): MeasurementValues {
  const values: MeasurementValues = { before: {}, after: {} }
  for (const phase of ['before', 'after'] as const) {
    for (const zone of MEASUREMENT_ZONES) {
      const text = input[phase][zone].trim()
      if (!text) continue
      const value = decimal(text)
      if (!value) throw new Error('Invalid measurement input')
      values[phase][zone] = canonical(value)
    }
  }
  return values
}

/** Preview only; the server recalculates every persisted delta. */
export function previewMeasurementDelta(
  before: string,
  after: string,
  rules: MeasurementInputRules,
): string | undefined {
  const bounds = checkedRules(rules)
  const first = decimal(before)
  const second = decimal(after)
  if (
    !first ||
    !second ||
    validationMessage(first, rules, bounds) ||
    validationMessage(second, rules, bounds)
  )
    return undefined
  const pair = aligned(first, second)
  return canonical({ coefficient: pair.right - pair.left, scale: pair.scale })
}
